import type { CSSProperties } from 'react';
import { strings } from '@/data/strings';
import { SectionHeader } from '@/components/SectionHeader';
import { AnimatedBlueBackdrop } from '@/components/AnimatedBlueBackdrop/AnimatedBlueBackdrop';
import { useInView } from '@/hooks/useInView';
import styles from './WhatIs.module.css';

function cx(...parts: Array<string | false | null | undefined>) {
  return parts.filter(Boolean).join(' ');
}

const ICONS = [IconFragmented, IconLocator, IconShield];

export function WhatIs() {
  const { ref, inView } = useInView<HTMLDivElement>({
    threshold: 0,
    rootMargin: '0px 0px -25% 0px',
  });

  return (
    <section id="que-es" className={styles.section}>
      <AnimatedBlueBackdrop />
      <div className="container">
        <SectionHeader
          align="center"
          eyebrow={strings.whatIs.eyebrow}
          title={strings.whatIs.title}
          subtitle={strings.whatIs.subtitle}
        />

        <div ref={ref} className={cx(styles.grid, inView && styles.revealed)}>
          {strings.whatIs.cards.map((card, i) => {
            const Icon = ICONS[i];
            return (
              <article
                key={card.title}
                data-tone={card.tone}
                className={styles.card}
                style={{ '--i': i } as CSSProperties}
              >
                <div className={styles.iconWrap} aria-hidden="true">
                  <Icon />
                </div>
                <h3 className={styles.cardTitle}>{card.title}</h3>
                <p className={styles.cardBody}>{card.body}</p>
              </article>
            );
          })}
        </div>
      </div>
    </section>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// Iconos — outline consistente, viewBox 32, stroke currentColor
// ─────────────────────────────────────────────────────────────────────────────

const ICON_PROPS = {
  width: 32,
  height: 32,
  viewBox: '0 0 32 32',
  fill: 'none',
  stroke: 'currentColor',
  strokeWidth: 1.8,
  strokeLinecap: 'round' as const,
  strokeLinejoin: 'round' as const,
};

function IconFragmented() {
  return (
    <svg {...ICON_PROPS}>
      <circle cx="7.5" cy="9" r="2.2" />
      <circle cx="24.5" cy="11" r="2.2" />
      <circle cx="14" cy="24" r="2.2" />
      <line x1="9.5" y1="9.4" x2="22.5" y2="10.6" strokeDasharray="2.5 2.5" />
      <line x1="8.5" y1="11" x2="13" y2="22" strokeDasharray="2.5 2.5" />
      <line x1="16" y1="23" x2="23.5" y2="13" strokeDasharray="2.5 2.5" />
    </svg>
  );
}

function IconLocator() {
  return (
    <svg {...ICON_PROPS}>
      <circle cx="16" cy="14" r="9" />
      <circle cx="16" cy="14" r="4" />
      <circle cx="16" cy="14" r="1.2" fill="currentColor" stroke="none" />
      <line x1="16" y1="23" x2="16" y2="28" />
    </svg>
  );
}

function IconShield() {
  return (
    <svg {...ICON_PROPS}>
      <path d="M16 4 L26 8 L26 16 C26 22 21.5 26.5 16 28 C10.5 26.5 6 22 6 16 L6 8 Z" />
      <path d="M11 16 L14.5 19.5 L21 12.5" />
    </svg>
  );
}
